import React, { useState, useEffect } from 'react';
import { Dialog, DialogFooter } from '../../components/ui/Dialog';
import CustomerStatusTracker from '../OrderTab/CustomerStatusTracker';
import { Button } from '../../components/ui/Button';
import {
  FileText,
  User,
  Building,
  CreditCard,
  Package,
  File,
  Users,
  Trash2,
  Eye,
  Edit,
  AlertTriangle,
  CheckCircle,
  X,
  Store,
  Info,
  Edit2,
  Printer,
  Smartphone,
  Landmark,
  Wallet,
  DollarSign,
  Check,
  FileX,
  FileImage,
  Image as ImageIcon,
  Loader,
  Edit3,
  ClipboardList,
  Clock,
  Building2
} from 'lucide-react';
import { OrderStatus, fetchOrderDocument, deleteOrderDocument, Order } from '../../services/order';
import { OrderDetail, OrderDocument } from '../../services/gatewayOrder';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { showToast } from '../../utils/toast';



interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderDetail | null;
  onUpdateOrder?: (updatedOrder: OrderDetail) => Promise<void>;
  canEdit: boolean;
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  order,
  onUpdateOrder,
  canEdit
}) => {
  // State for editing UTR info
  const [isEditingUtr, setIsEditingUtr] = useState(false);
  const [utrNumber, setUtrNumber] = useState('');
  const [utrReason, setUtrReason] = useState('');

  // State for transaction ID
  const [transactionId, setTransactionId] = useState('');
  const [isEditingTransactionId, setIsEditingTransactionId] = useState(false);

  // State for deleting documents
  const [documentToDelete, setDocumentToDelete] = useState<OrderDocument | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // State for changing order status
  const [newOrderStatus, setNewOrderStatus] = useState<OrderStatus | ''>('');
  const [isChangingStatus, setIsChangingStatus] = useState(false);

  // State for active tab
  const [activeTab, setActiveTab] = useState('details');

  // State for document viewing and deleting
  const [selectedDocType, setSelectedDocType] = useState<'docProduct' | 'docReceipt' | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [isConfirmingDocDelete, setIsConfirmingDocDelete] = useState(false);

  // Reset states when order changes
  useEffect(() => {
    if (order) {
      setUtrNumber(order.utr || '');
      setUtrReason(order.utrReason || '');
      setTransactionId(order.transaction?.transactionId || '');
      setNewOrderStatus('');
      setSelectedDocType(null);
      setDocumentUrl(null);

      // Log keyFactStatement data for debugging
      if (order.keyFactStatement) {
        console.log('Debug - keyFactStatement:', order.keyFactStatement);
      } else {
        console.log('Debug - No keyFactStatement data found in order');
      }
    }
  }, [order]);

  // Fetch document when document type changes
  useEffect(() => {
    if (order && selectedDocType) {
      fetchDocumentUrl(order.orderId, selectedDocType);
    }
  }, [selectedDocType, order]);

  if (!order) return null;

  // Helper functions for payment display
  const getPaymentStatusVariant = (status: string): "default" | "outline" | "secondary" | "destructive" | "success" => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
      case 'successful':
        return 'success';
      case 'pending':
      case 'in process':
      case 'processing':
        return 'secondary';
      case 'failed':
      case 'declined':
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'credit card':
        return <CreditCard size={16} className="mr-1" />;
      case 'debit card':
        return <CreditCard size={16} className="mr-1" />;
      case 'upi':
        return <Smartphone size={16} className="mr-1" />;
      case 'netbanking':
        return <Landmark size={16} className="mr-1" />;
      case 'wallet':
        return <Wallet size={16} className="mr-1" />;
      case 'cash':
        return <DollarSign size={16} className="mr-1" />;
      default:
        return <CreditCard size={16} className="mr-1" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string, includeTime: boolean = false) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };

    if (includeTime) {
      return new Intl.DateTimeFormat('en-IN', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  // Helper function to convert timestamps to formatted dates
  const formatTimestamp = (timestamp?: number, includeTime: boolean = false) => {
    if (!timestamp) return 'N/A';

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    };

    if (includeTime) {
      return new Intl.DateTimeFormat('en-IN', {
        ...options,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).format(date);
    }

    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  // Handle UTR update
  const handleUtrUpdate = async () => {
    if (!order || !onUpdateOrder) return;

    try {
      console.log('Updating UTR with values:', {
        utrNumber,
        utrReason
      });

      // Create updatedOrder with only the fields that have changed
      const updatedOrder = {
        ...order,
        utrUpdatedAt: new Date().toISOString(),
        utrBy: "Current User" // In real app, get from auth context
      };

      // Only include values that are being updated
      if (utrNumber !== order.utr) {
        updatedOrder.utr = utrNumber;
      }

      if (utrReason !== order.utrReason) {
        updatedOrder.utrReason = utrReason;
      }

      await onUpdateOrder(updatedOrder);
      setIsEditingUtr(false);
    } catch (error) {
      console.error('Error updating UTR:', error);
      // Handle error (show error message)
    }
  };

  // Handle document delete
  const handleDocumentDelete = async () => {
    if (!order || !documentToDelete || !onUpdateOrder) {
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
      return;
    }

    try {
      // Filter out the document to delete
      const updatedDocuments = (order.documents || []).filter(
        doc => doc.id !== documentToDelete.id
      );

      const updatedOrder = {
        ...order,
        documents: updatedDocuments
      };

      await onUpdateOrder(updatedOrder);
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      // Handle error (show error message)
    }
  };

  // Handle order status change
  const handleStatusChange = async () => {
    if (!order || !newOrderStatus || !onUpdateOrder) return;

    try {
      // Check if status is changing to "Verification Pending" and transaction ID is missing
      if (newOrderStatus === "Verification Pending" && !transactionId && !order.transaction?.transactionId) {
        setIsEditingTransactionId(true);
        return;
      }

      console.log('Changing order status to:', newOrderStatus);

      const updatedOrder = {
        ...order,
        status: newOrderStatus,
        // Always include txnId if status is Verification Pending
        ...(newOrderStatus === "Verification Pending" ? {
          txnId: transactionId || order.transaction?.transactionId || ''
        } : {})
      };

      await onUpdateOrder(updatedOrder);
      setIsChangingStatus(false);
      setNewOrderStatus('');
    } catch (error) {
      console.error('Error changing order status:', error);
      // Handle error (show error message)
    }
  };

  // Handle transaction ID update
  const handleTransactionIdUpdate = async () => {
    if (!order || !onUpdateOrder) return;

    try {
      console.log('Updating Transaction ID with value:', transactionId);

      // Create a properly typed transaction object
      const updatedOrder = {
        ...order,
        txnId: transactionId,
        // If status is already Verification Pending, keep it, otherwise change to Verification Pending
        status: order.status === "Verification Pending" ? order.status : "Verification Pending" as OrderStatus
      };

      console.log('Sending updated order with txnId:', updatedOrder);
      await onUpdateOrder(updatedOrder);
      setIsEditingTransactionId(false);
    } catch (error) {
      console.error('Error updating Transaction ID:', error);
      // Handle error
    }
  };

  // Function to view/download invoice
  const handleViewInvoice = () => {
    // Generate invoice URL from order ID
    try {
      // Direct to invoice page with order ID
      const invoiceUrl = `/invoice/${order.orderId}`;
      window.open(invoiceUrl, '_blank');
    } catch (error) {
      console.error("Error opening invoice:", error);
    }
  };

  // Render status badge
  const renderStatusBadge = (status: OrderStatus) => {
    let bgColor = '';
    let textColor = '';

    switch (status) {
      case 'In Process':
        bgColor = 'bg-cyan-100';
        textColor = 'text-cyan-800';
        break;
      case 'Expired':
        bgColor = 'bg-gray-100';
        textColor = 'text-gray-800';
        break;
      case 'Product Verified':
        bgColor = 'bg-teal-100';
        textColor = 'text-teal-800';
        break;
      case 'QR Activated':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'Verification Pending':
        bgColor = 'bg-indigo-100';
        textColor = 'text-indigo-800';
        break;
      case 'Failed':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'Approved':
        bgColor = 'bg-orange-100';
        textColor = 'text-orange-800';
        break;
      case 'Completed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'Cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-900';
        break;
      default:
        bgColor = 'bg-transparent';
        textColor = 'text-gray-800';
    }

    return (
      <span className={`px-2 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor} capitalize`}>
        {status}
      </span>
    );
  };


  const handleEditOrder = () => {
    // Implementation for editing the order
    onClose();
    // Navigate to edit page or open edit modal
  };

  // Add formatFileSize function before other helper functions
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Add currentUserRole variable
  const currentUserRole = 'ADMIN'; // Temporary fix, ideally would be from auth context

  // Updated helper function for rendering customer name
  const getCustomerName = () => {
    if (order.customerId?.name) return order.customerId.name;
    if (order.settlement?.customerName) return order.settlement.customerName;
    return 'Customer';
  };

  // Updated helper function for rendering customer initial
  const getCustomerInitial = () => {
    const name = getCustomerName();
    return name.charAt(0);
  };

  // Update the hasUtr function to check both places where UTR might exist
  const hasUtr = () => {
    return Boolean(
      (order.utr && order.utr.trim().length > 0) ||
      (order.settlement?.utr && order.settlement.utr.trim().length > 0)
    );
  };

  // Apply CSS class conditionally
  const cn = (...classes: (string | boolean | undefined)[]) => {
    return classes.filter(Boolean).join(' ');
  };

  // New function to fetch document URL
  const fetchDocumentUrl = async (orderId: string, docType: 'docProduct' | 'docReceipt') => {
    setIsLoadingDocument(true);
    try {
      const token = localStorage.getItem("token");
      const url = await fetchOrderDocument(orderId, docType, token);
      setDocumentUrl(url);
    } catch (error) {
      console.error(`Error fetching ${docType}:`, error);
      setDocumentUrl(null);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  // Fix the delete document function for docProduct and docReceipt
  const handleDeleteDocument = async () => {
    if (!order || !selectedDocType) return;

    setIsLoadingDocument(true);
    try {
      const token = localStorage.getItem("token");
      const success = await deleteOrderDocument(order.orderId, selectedDocType, token);

      if (success) {
        setDocumentUrl(null);
        setSelectedDocType(null);
        setIsConfirmingDocDelete(false);

        // Refresh the order if there's an update function
        if (onUpdateOrder) {
          // Create an updated order object with the document field set to empty string
          const updatedOrder = {
            ...order,
            // Update the appropriate field based on which document was deleted
            ...(selectedDocType === 'docProduct' ? {
              docProduct: "",
              // Change status to Verification Pending when product image is deleted
              status: "Verification Pending" as OrderStatus
            } : {}),
            ...(selectedDocType === 'docReceipt' ? { docReceipt: "" } : {}),
          };
          await onUpdateOrder(updatedOrder);
        }
      }
    } catch (error) {
      console.error(`Error deleting ${selectedDocType}:`, error);
    } finally {
      setIsLoadingDocument(false);
    }
  };

  const dpTransactions = order?.dpTransaction || [];

  const selectedDpTransaction = (() => {
    if (!dpTransactions.length) return null;

    const sortedTransactions = [...dpTransactions].sort((a, b) => {
      const dateA = new Date(a.txntime || a.updatedAt || 0).getTime();
      const dateB = new Date(b.txntime || b.updatedAt || 0).getTime();

      return dateB - dateA;
    });

    const latestSuccessfulTransaction = sortedTransactions.find(
      (txn) => txn.status?.toLowerCase() === 'success'
    );

    return latestSuccessfulTransaction || sortedTransactions[0];
  })();

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Order Details: ${order?.orderId || 'Loading...'}`}
      size="xl"
    >
      <div className="space-y-6 max-h-[80vh] overflow-y-auto p-1 no-scrollbar">
        {/* Enhanced Header with Customer Name, Order Status, and Creation Date */}
        <div className="flex items-center mb-6">
          <div className="flex items-center justify-center w-12 h-12 mr-3 text-xl font-bold text-white rounded-full bg-gradient-to-r from-blue-500 to-indigo-600">
            {getCustomerInitial()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{getCustomerName()}</h2>
            <div className="flex items-center mt-1 text-sm">
              <span className="mr-2 text-gray-500">OrderID: {order.orderId}</span>
              {renderStatusBadge(order.status)}
              <span className="ml-2 text-gray-500">
                Created: {formatDate(order.createdAt)}
              </span>
            </div>
          </div>

          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              onClick={handleViewInvoice}
              className="flex items-center gap-2"
            >
              <FileText size={16} className="text-blue-600" />
              View Invoice
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="details">Order Details</TabsTrigger>
            <TabsTrigger value="customers">Customer Info</TabsTrigger>
            <TabsTrigger value="merchant">Merchant Info</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="payment">Payment Details</TabsTrigger>
            {order?.lender !== 'cashe' && <TabsTrigger value="status">Lender Journey Status</TabsTrigger>}
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>

          {/* Order Details Tab */}
          <TabsContent value="details" className="space-y-6 " >
            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-blue-100 rounded-full">
                  <FileText className="text-blue-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Order Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Order ID</p>
                  <p className="font-medium">{order.orderId}</p>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Order Status</p>
                    {canEdit && !isChangingStatus && (
                      <button
                        onClick={() => setIsChangingStatus(true)}
                        className="p-1 text-blue-600 rounded hover:text-blue-800 bg-blue-50"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                  </div>
                  {isChangingStatus ? (
                    <div className="mt-1 space-y-2">
                      <select
                        value={newOrderStatus}
                        onChange={(e) => setNewOrderStatus(e.target.value as OrderStatus)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Status</option>
                        <option value="In Process">In Process</option>
                        <option value="Expired">Expired</option>
                        <option value="Product Verified">Product Verified</option>
                        <option value="QR Activated">QR Activated</option>
                        <option value="Verification Pending">Verification Pending</option>
                        <option value="Failed">Failed</option>
                        <option value="Approved">Approved</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>

                      {/* Show transaction ID field when status is Verification Pending and transactionId is empty */}
                      {newOrderStatus === "Verification Pending" &&
                        !order.transaction?.transactionId && (
                          <div className="mt-2">
                            <label className="block mb-1 text-xs font-medium text-gray-700">
                              Transaction ID (required for Verification Pending status)
                            </label>
                            <Input
                              value={transactionId}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTransactionId(e.target.value)}
                              className="h-8 text-sm"
                              placeholder="Enter Transaction ID"
                            />
                          </div>
                        )}

                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={handleStatusChange}
                          disabled={!newOrderStatus || (newOrderStatus === "Verification Pending" && !transactionId && !order.transaction?.transactionId)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Update
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsChangingStatus(false);
                            setNewOrderStatus('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="font-medium">{renderStatusBadge(order.status)}</p>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Transaction Status</p>
                  {/* <p className="font-medium">{hasUtr() ? 'True' : 'False'}</p> */}
                  <p className="font-medium">{order?.transaction?.transactionStatus || 'N/A'}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Link Activated At</p>
                  <p className="font-medium">{formatTimestamp(order.linkActivatedAt, true)}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Transaction Time</p>
                  <p className="font-medium">{order.transaction?.transactionTime ? formatTimestamp(order.transaction.transactionTime, true) : 'N/A'}</p>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Transaction ID</p>
                    {canEdit && !isEditingTransactionId && (
                      <button
                        onClick={() => setIsEditingTransactionId(true)}
                        className="p-1 text-blue-600 rounded hover:text-blue-800 bg-blue-50"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                  </div>
                  {isEditingTransactionId ? (
                    <div className="mt-1 space-y-2">
                      <Input
                        value={transactionId}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTransactionId(e.target.value)}
                        className="h-8 text-sm"
                        placeholder="Enter Transaction ID"
                      />
                      <div className="flex space-x-2">
                        <Button size="sm" onClick={handleTransactionIdUpdate} disabled={!transactionId} className="bg-blue-600 hover:bg-blue-700">
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsEditingTransactionId(false);
                            setTransactionId(order.transaction?.transactionId || '');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="font-medium">{order.transaction?.transactionId || 'Not Available'}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR Number</p>
                    {canEdit && !isEditingUtr && (
                      <button
                        onClick={() => setIsEditingUtr(true)}
                        className="p-1 text-blue-600 rounded hover:text-blue-800 bg-blue-50"
                      >
                        <Edit size={14} />
                      </button>
                    )}
                  </div>
                  {isEditingUtr ? (
                    <div className="mt-1 space-y-2">
                      <Input
                        value={utrNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUtrNumber(e.target.value)}
                        className="h-8 text-sm"
                        placeholder="Enter UTR Number"
                      />
                      <input
                        type="text"
                        value={utrReason}
                        onChange={(e) => setUtrReason(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter UTR Reason"
                      />
                      <div className="flex space-x-2">
                        <Button size="sm" onClick={handleUtrUpdate} disabled={!utrNumber && !utrReason} className="bg-blue-600 hover:bg-blue-700">
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setIsEditingUtr(false);
                            setUtrNumber(order.utr || '');
                            setUtrReason(order.utrReason || '');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="font-medium">{order.utr || 'Not Available'}</p>
                  )}
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR Updated At</p>
                  <p className="font-medium">
                    {typeof order.utrUpdatedAt === 'number'
                      ? formatTimestamp(order.utrUpdatedAt, true)
                      : order.utrUpdatedAt
                        ? formatDate(order.utrUpdatedAt, true)
                        : (order.settlement?.utrAt ? formatDate(order.settlement.utrAt, true) : 'Not Updated')}
                  </p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR By</p>
                  <p className="font-medium">{order.utrBy || 'Not Available'}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR Reason</p>
                  <p className="font-medium">{order.utrReason || 'Not Available'}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Lender</p>
                  <p className="font-medium">{order.lender}</p>
                </div>
              </div>
            </div>

            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-purple-100 rounded-full">
                  <Info className="text-purple-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Created At</p>
                  <p className="font-medium">{formatDate(order.createdAt, true)}</p>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Updated At</p>
                  <p className="font-medium">{formatDate(order.updatedAt ?? '', true)}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Amount</p>
                  <p className="font-medium">{formatCurrency(order.amount || order.settlement?.totalAmount || 0)}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Customer Information Tab */}
          <TabsContent value="customers" className="space-y-6">
            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-green-100 rounded-full">
                  <User className="text-green-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Customer Name</p>
                  <p className="font-medium">{order.customerId?.name || order.settlement?.customerName}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                  <p className="font-medium">{order.customerId?.mobileNo || order.settlement?.customerMobileNo}</p>
                </div>

                {order.customerId?.monthlyIncome && (
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Income</p>
                    <p className="font-medium">
                      {formatCurrency(order.customerId.monthlyIncome)}
                    </p>
                  </div>
                )}

                {order.customerId?.employmentType !== undefined && (
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Employment Type</p>
                    <p className="font-medium">
                      {order.customerId.employmentType === 1 ? 'Salaried' :
                        order.customerId.employmentType === 2 ? 'Self-Employed' :
                          order.customerId.employmentType === 3 ? 'Business Owner' :
                            String(order.customerId.employmentType)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Merchant Information Tab */}
          <TabsContent value="merchant" className="space-y-6">
            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-purple-100 rounded-full">
                  <Store className="text-purple-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Merchant Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Merchant Name</p>
                  <p className="font-medium">{order.merchantId?.name || order.settlement?.merchantName}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                  <p className="font-medium">{order.merchantId?.mobileNo || order.settlement?.merchantMobileNo}</p>
                </div>

                <div>
                  <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Email</p>
                  <p className="font-medium">{order.merchantId?.email || order.settlement?.merchantEmail || 'Not Available'}</p>
                </div>

                {order.merchantId?.kycBank && (
                  <>
                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Bank Account</p>
                      <p className="font-medium">{order.merchantId.kycBank.accountNo || 'Not Available'}</p>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">IFSC Code</p>
                      <p className="font-medium">{order.merchantId.kycBank.ifsc || 'Not Available'}</p>
                    </div>

                    <div>
                      <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Account Holder</p>
                      <p className="font-medium">{order.merchantId.kycBank.holderName || 'Not Available'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Partner Information (if available) */}
            {order.partnerId && (
              <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                  <div className="p-2 mr-3 bg-yellow-100 rounded-full">
                    <Users className="text-yellow-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Partner Information</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Partner Name</p>
                    <p className="font-medium">{order.partnerId.name || order.settlement?.partnerName}</p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Mobile Number</p>
                    <p className="font-medium">{order.partnerId.mobileNo}</p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">Email</p>
                    <p className="font-medium">{order.partnerId.email || 'Not Available'}</p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-red-100 rounded-full">
                  <Package className="text-red-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Product Details</h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Product Details
                      </th>
                      <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Product Brand
                      </th>
                      <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Total Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr className="bg-white">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {order.product?.name || order.settlement?.productName || order.settlement?.productDetails || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {order.variationString || order.settlement?.productBrand || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatCurrency(order.amount || order.settlement?.totalAmount || 0)}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={2} className="px-6 py-3 text-sm font-medium text-right text-gray-500">
                        Order Total:
                      </td>
                      <td className="px-6 py-3 text-sm font-bold text-gray-900 whitespace-nowrap">
                        {formatCurrency(order.amount || order.settlement?.totalAmount || 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-indigo-100 rounded-full">
                  <FileText className="text-indigo-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Order Documents</h3>
              </div>

              <div className="flex flex-col gap-6 md:flex-row">
                {/* Document Type Selection */}
                <div className="w-full space-y-4 md:w-1/3">
                  <h4 className="font-medium text-gray-700">Document Type</h4>

                  <div className="space-y-3">
                    <button
                      className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docProduct'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      onClick={() => setSelectedDocType('docProduct')}
                    >
                      <div className="p-2 bg-blue-100 rounded-full">
                        <FileImage size={18} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">Product Image</p>
                        <p className="text-xs text-gray-500">View the product image</p>
                      </div>
                    </button>

                    <button
                      className={`w-full text-left p-3 rounded-lg border flex items-center space-x-3 transition-colors ${selectedDocType === 'docReceipt'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      onClick={() => setSelectedDocType('docReceipt')}
                    >
                      <div className="p-2 bg-green-100 rounded-full">
                        <File size={18} className="text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">Receipt</p>
                        <p className="text-xs text-gray-500">View payment receipt</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Document Display Area */}
                <div className="w-full md:w-2/3 border rounded-lg p-4 min-h-[400px] flex items-center justify-center bg-gray-50">
                  {isLoadingDocument ? (
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <Loader className="mb-3 animate-spin" size={32} />
                      <p>Loading document...</p>
                    </div>
                  ) : selectedDocType ? (
                    documentUrl ? (
                      <div className="flex flex-col w-full h-full">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium">
                            {selectedDocType === 'docProduct' ? 'Product Image' : 'Receipt'}
                          </h4>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(documentUrl, '_blank')}
                              className="flex items-center gap-1"
                            >
                              <Eye size={16} />
                              Open
                            </Button>

                            {canEdit && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => setIsConfirmingDocDelete(true)}
                                className="flex items-center gap-1"
                              >
                                <Trash2 size={16} />
                                Delete
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-center flex-grow overflow-hidden bg-white border border-gray-200 rounded-lg">
                          <img
                            src={documentUrl}
                            alt={selectedDocType === 'docProduct' ? 'Product Image' : 'Receipt'}
                            className="max-w-full max-h-[500px] object-contain"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <FileX size={48} className="mb-3" />
                        <p className="font-medium">Document not available</p>
                        <p className="text-sm">The requested document could not be found</p>
                      </div>
                    )
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <ImageIcon size={48} className="mb-3" />
                      <p className="font-medium">Select a document to view</p>
                      <p className="text-sm">Choose a document type from the left panel</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                <div className="p-2 mr-3 bg-green-100 rounded-full">
                  <CreditCard className="text-green-600" size={18} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Loan Details</h3>
              </div>

              {order.keyFactStatement ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">

                  {/* Scheme & Loan Basics */}
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Scheme Name
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.scheme?.name || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Loan Tenure
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.calculations?.grossTenure
                        ? `${order.keyFactStatement.calculations.grossTenure} months`
                        : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Annual Interest
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.scheme?.annualInterest !== undefined
                        ? `${order.keyFactStatement.scheme.annualInterest}%`
                        : 'N/A'}
                    </p>
                  </div>

                  {/* Amounts */}
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Total Amount
                    </p>
                    <p className="font-medium text-green-600">
                      {formatCurrency(order.amount || 0)}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Net Loan Amount
                    </p>
                    <p className="font-medium">
                      {formatCurrency(
                        order.keyFactStatement?.calculations?.netLoanAmount ||
                        order.amount ||
                        0
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      {order?.category === "INS"
                        ? "Disbursement To Insurer"
                        : "DBD To Dealer"}
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.calculations?.disbursementToInsurer !== undefined
                        ? formatCurrency(
                          order.keyFactStatement.calculations.disbursementToInsurer
                        )
                        : "N/A"}
                    </p>
                  </div>

                  {/* Customer Contribution */}
                  {order?.category === "INS" ? (
                    <>
                      <div>
                        <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                          DP Percentage
                        </p>
                        <p className="font-medium">
                          {order?.keyFactStatement?.calculations?.partnerDownPaymentPercentage ?? 'N/A'}%
                        </p>
                      </div>

                      <div>
                        <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                          DP Amount
                        </p>
                        <p className="font-medium">
                          {selectedDpTransaction?.dpAmount
                            ? formatCurrency(selectedDpTransaction.dpAmount)
                            : 'N/A'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                          Advanced EMI
                        </p>
                        <p className="font-medium">
                          {order.keyFactStatement?.scheme?.advancedEMI ?? 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                          Advanced EMI Payment
                        </p>
                        <p className="font-medium">
                          {order.keyFactStatement?.calculations?.advancedEmiPayment !== undefined
                            ? formatCurrency(
                              order.keyFactStatement.calculations.advancedEmiPayment
                            )
                            : 'N/A'}
                        </p>
                      </div>
                    </>
                  )}

                  {/* EMI */}
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Monthly EMI Amount
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.calculations?.monthlyEMI
                        ? formatCurrency(order.keyFactStatement.calculations.monthlyEMI)
                        : 'N/A'}
                    </p>
                  </div>

                  {/* Charges */}
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Processing Fee
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.calculations?.totalProcessingFee !== undefined
                        ? formatCurrency(order.keyFactStatement.calculations.totalProcessingFee)
                        : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      GST
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.scheme?.GST !== undefined
                        ? formatCurrency(order.keyFactStatement.scheme.GST)
                        : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Total Interest Payable
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.calculations?.totalInterestPayable !== undefined
                        ? formatCurrency(
                          order.keyFactStatement.calculations.totalInterestPayable
                        )
                        : 'N/A'}
                    </p>
                  </div>

                  <div className="col-span-3">
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Subvention Cost
                    </p>
                    <p className="font-medium">
                      {order.keyFactStatement?.scheme?.subventionCost !== undefined
                        ? `${order.keyFactStatement.scheme.subventionCost}%`
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="flex items-center justify-center w-12 h-12 mb-3 bg-gray-100 rounded-full">
                    <AlertTriangle size={24} className="text-amber-500" />
                  </div>
                  <p className="mb-1 font-medium text-gray-800">No loan details available</p>
                  <p className="max-w-md text-sm text-center text-gray-500">
                    The Key Fact Statement for this order is missing or not available. This might happen for orders created before the feature was implemented.
                  </p>
                </div>
              )}

              {/* <div className="pt-6 mt-6 border-t border-gray-200">
                <h4 className="mb-4 text-sm font-medium text-gray-800">UTR Information</h4>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR Number</p>
                    {isEditingUtr ? (
                      <div className="flex items-center mt-1 space-x-2">
                        <Input
                          value={utrNumber}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUtrNumber(e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Enter UTR Number"
                        />
                        <Button size="sm" variant="outline" onClick={handleUtrUpdate}>
                          <Check size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setIsEditingUtr(false)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <p className="mr-2 font-medium">{order.utr || order.settlement?.utr || 'N/A'}</p>
                        {canEdit && (
                          <Button size="sm" variant="ghost" className="px-1.5 h-6" onClick={() => setIsEditingUtr(true)}>
                            <Edit size={14} />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR Updated At</p>
                    <p className="font-medium">
                      {typeof order.utrUpdatedAt === 'number'
                        ? formatTimestamp(order.utrUpdatedAt, true)
                        : order.utrUpdatedAt
                          ? formatDate(order.utrUpdatedAt, true)
                          : (order.settlement?.utrAt ? formatDate(order.settlement.utrAt, true) : 'Not Updated')}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">UTR Reason</p>
                    <p className="font-medium">{order.utrReason || order.settlement?.utrReason || 'Not Available'}</p>
                  </div>
                </div>
              </div> */}


            </div>
            {order?.category === "INS" && (
              <div className="p-5 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex items-center pb-3 mb-4 border-b border-gray-100">
                  <div className="p-2 mr-3 bg-blue-100 rounded-full">
                    <CreditCard className="text-blue-600" size={18} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Down Payment Details
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">


                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Down Payment Amount
                    </p>
                    <p className="font-medium">
                      ₹{selectedDpTransaction?.dpAmount ?? "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Transaction Status
                    </p>

                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${selectedDpTransaction?.status?.toLowerCase() === "success"
                        ? "bg-green-100 text-green-700"
                        : selectedDpTransaction?.status?.toLowerCase() === "failed" ||
                          selectedDpTransaction?.status?.toLowerCase() === "failure"
                          ? "bg-red-100 text-red-700"
                          : selectedDpTransaction?.status?.toLowerCase() === "initiated"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                    >
                      {selectedDpTransaction?.status || "N/A"}
                    </span>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      DP Transaction ID
                    </p>
                    <p className="font-medium break-all">
                      {selectedDpTransaction?.txnid || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Easepay ID
                    </p>
                    <p className="font-medium">
                      {selectedDpTransaction?.dpResponse?.[0]?.surl?.easepayid ?? "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Payment Mode
                    </p>
                    <p className="font-medium">
                      {selectedDpTransaction?.dpResponse?.[0]?.surl?.card_type ?? "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-500 uppercase">
                      Transaction Time
                    </p>
                    <p className="font-medium">
                      {selectedDpTransaction?.txntime
                        ? formatDate(selectedDpTransaction.txntime, true)
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="status" className="space-y-6">
            <CustomerStatusTracker order={order} activeTab={activeTab} />
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-6">
            <div className="overflow-hidden bg-white border border-gray-200 rounded-md">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-sm font-medium text-gray-700">Logs</h3>
              </div>

              {order.logs && order.logs.length > 0 ? (
                <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                  {order.logs.slice().reverse().map((log, index) => (
                    <div key={index} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start">
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 mr-3 text-blue-600 bg-blue-100 rounded-full">
                          <ClipboardList size={16} />
                        </div>
                        <div>
                          <p className="text-sm text-gray-700">{log}</p>
                          <div className="flex items-center mt-1 text-xs text-gray-500">
                            <Clock size={12} className="mr-1" />
                            <span>
                              {log.includes(" at ") ? log.split(" at ")[1] : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="flex items-center justify-center w-16 h-16 mb-4 text-gray-400 bg-gray-100 rounded-full">
                    <ClipboardList size={24} />
                  </div>
                  <p className="text-sm text-gray-500">No logs are available for this order</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Document Confirmation Dialog */}
      {isConfirmingDocDelete && selectedDocType && (
        <Dialog
          isOpen={isConfirmingDocDelete}
          onClose={() => setIsConfirmingDocDelete(false)}
          title="Confirm Document Deletion"
          size="sm"
        >
          <div className="p-4 space-y-4">
            <div className="flex items-start">
              <AlertTriangle size={24} className="flex-shrink-0 mr-3 text-red-500" />
              <div>
                <h4 className="font-medium">Are you sure you want to delete this document?</h4>
                <p className="mt-1 text-gray-500">
                  You are about to delete the {selectedDocType === 'docProduct' ? 'product image' : 'receipt'}.
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsConfirmingDocDelete(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteDocument}
                disabled={isLoadingDocument}
              >
                {isLoadingDocument ? (
                  <>
                    <Loader size={16} className="mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Document'
                )}
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Original Delete Document Confirmation Dialog */}
      {showDeleteConfirm && documentToDelete && (
        <Dialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDocumentToDelete(null);
          }}
          title="Confirm Document Deletion"
          size="sm"
        >
          <div className="p-4 space-y-4">
            <div className="flex items-start">
              <AlertTriangle size={24} className="flex-shrink-0 mr-3 text-red-500" />
              <div>
                <h4 className="font-medium">Are you sure you want to delete this document?</h4>
                <p className="mt-1 text-gray-500">
                  You are about to delete the document "{documentToDelete.label}". This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4 space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDocumentToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDocumentDelete}
              >
                Delete Document
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}; 